import type { VerificationIconKey } from '../constants/verificationMode';

// ---------------------------------------------------------------------------
// ModeEntry — one row of the canonical lookup table
// ---------------------------------------------------------------------------

export interface ModeEntry {
  /** i18n key for the verification method (biometric or interaction type) */
  methodKey: string;
  /** i18n key for the default source (device / app category) */
  sourceKey: string;
  /**
   * i18n key override for source when payload_timelog.source === 'clock-web'.
   * Only defined for IDs where 'clock-web' produces a different label than sourceKey.
   */
  sourceWithClockWeb?: string;
  /**
   * i18n key override for source when payload_timelog.source === 'clock-mobile'.
   * Only defined for IDs where 'clock-mobile' produces a different label than sourceKey.
   */
  sourceWithClockMobile?: string;
  /** Icon key for the verification METHOD. Never overridden by payloadSource. */
  methodIconKey: VerificationIconKey;
  /** Icon key for the default source. */
  sourceIconKey: VerificationIconKey;
  /** Icon key when source is overridden by 'clock-web'. */
  sourceIconKeyWithClockWeb?: VerificationIconKey;
  /** Icon key when source is overridden by 'clock-mobile'. */
  sourceIconKeyWithClockMobile?: VerificationIconKey;
}

// ---------------------------------------------------------------------------
// VERIFICATION_MODE_TABLE — canonical 17-entry lookup
// ---------------------------------------------------------------------------
//
// methodKey prefix:  timelog.method.*
// sourceKey prefix:  timelog.source.*
//
// IDs 1–17 correspond to id_verification_mode from the backend.
// Source defaults are the generic device category for that mode.
// Clock-web/mobile overrides are only set when the clock-source changes the
// displayed source (e.g. id=11 "App Web" → "Clock Web").
// ---------------------------------------------------------------------------

export const VERIFICATION_MODE_TABLE: Record<number, ModeEntry> = {
  // Biometric terminal modes (1–8, 17)
  1: {
    methodKey: 'timelog.method.fingerprint',
    sourceKey: 'timelog.source.biometric',
    methodIconKey: 'fingerprint',
    sourceIconKey: 'biometric',
  },
  2: {
    methodKey: 'timelog.method.card',
    sourceKey: 'timelog.source.biometric',
    sourceWithClockWeb: 'timelog.source.clockWeb',
    methodIconKey: 'card',
    sourceIconKey: 'biometric',
    sourceIconKeyWithClockWeb: 'clockWeb',
  },
  3: {
    methodKey: 'timelog.method.pin',
    sourceKey: 'timelog.source.biometric',
    methodIconKey: 'pin',
    sourceIconKey: 'biometric',
  },
  4: {
    methodKey: 'timelog.method.facial',
    sourceKey: 'timelog.source.biometric',
    methodIconKey: 'facial',
    sourceIconKey: 'biometric',
  },
  5: {
    methodKey: 'timelog.method.iris',
    sourceKey: 'timelog.source.biometric',
    methodIconKey: 'iris',
    sourceIconKey: 'biometric',
  },
  6: {
    methodKey: 'timelog.method.fingerprintCard',
    sourceKey: 'timelog.source.biometric',
    methodIconKey: 'fingerprint',
    sourceIconKey: 'biometric',
  },
  7: {
    methodKey: 'timelog.method.fingerprintCardPin',
    sourceKey: 'timelog.source.biometric',
    methodIconKey: 'fingerprint',
    sourceIconKey: 'biometric',
  },
  8: {
    methodKey: 'timelog.method.plate',
    sourceKey: 'timelog.source.biometric',
    methodIconKey: 'plate',
    sourceIconKey: 'biometric',
  },
  // Manual / user entry (9)
  9: {
    methodKey: 'timelog.method.user',
    sourceKey: 'timelog.source.manual',
    methodIconKey: 'user',
    sourceIconKey: 'manual',
  },
  // App-based modes (10–16)
  10: {
    methodKey: 'timelog.method.facial',
    sourceKey: 'timelog.source.appMobile',
    sourceWithClockMobile: 'timelog.source.clockMobile',
    methodIconKey: 'facial',
    sourceIconKey: 'appMobile',
    sourceIconKeyWithClockMobile: 'clockMobile',
  },
  11: {
    methodKey: 'timelog.method.facial',
    sourceKey: 'timelog.source.appWeb',
    sourceWithClockWeb: 'timelog.source.clockWeb',
    methodIconKey: 'facial',
    sourceIconKey: 'appWeb',
    sourceIconKeyWithClockWeb: 'clockWeb',
  },
  12: {
    methodKey: 'timelog.method.fingerprint',
    sourceKey: 'timelog.source.appWeb',
    methodIconKey: 'fingerprint',
    sourceIconKey: 'appWeb',
  },
  13: {
    methodKey: 'timelog.method.pin',
    sourceKey: 'timelog.source.appWeb',
    sourceWithClockWeb: 'timelog.source.clockWeb',
    methodIconKey: 'pin',
    sourceIconKey: 'appWeb',
    sourceIconKeyWithClockWeb: 'clockWeb',
  },
  14: {
    methodKey: 'timelog.method.pin',
    sourceKey: 'timelog.source.appMobile',
    sourceWithClockMobile: 'timelog.source.clockMobile',
    methodIconKey: 'pin',
    sourceIconKey: 'appMobile',
    sourceIconKeyWithClockMobile: 'clockMobile',
  },
  15: {
    methodKey: 'timelog.method.pc',
    sourceKey: 'timelog.source.appWeb',
    methodIconKey: 'pc',
    sourceIconKey: 'appWeb',
  },
  16: {
    methodKey: 'timelog.method.mobile',
    sourceKey: 'timelog.source.appMobile',
    methodIconKey: 'mobile',
    sourceIconKey: 'appMobile',
  },
  17: {
    methodKey: 'timelog.method.palm',
    sourceKey: 'timelog.source.biometric',
    methodIconKey: 'palm',
    sourceIconKey: 'biometric',
  },
};

// ---------------------------------------------------------------------------
// Sentinel values for unknown / null ids
// ---------------------------------------------------------------------------

const SENTINEL_METHOD = '—';
const SENTINEL_SOURCE = '—';
const SENTINEL_ICON: VerificationIconKey = 'unknown';

// ---------------------------------------------------------------------------
// resolveVerificationLabels — public API
// ---------------------------------------------------------------------------

export interface VerificationLabels {
  method: string;
  source: string;
  methodIconKey: VerificationIconKey;
  sourceIconKey: VerificationIconKey;
}

/**
 * Resolve the display labels and icon keys for a timelog's verification mode.
 *
 * @param id           - Backend `id_verification_mode` (1–17). null/undefined → sentinel.
 * @param payloadSource - `payload_timelog.source` string from the API. Null/empty → table default.
 *   Comparison is case-insensitive and trims leading/trailing whitespace — the backend may
 *   store the source as uppercase (e.g. 'CLOCK-WEB') even though we send lowercase.
 * @param t            - i18next translation function (or any `(key: string) => string`).
 * @returns `{ method, source, methodIconKey, sourceIconKey }`
 *   - `method` is NEVER altered by `payloadSource`.
 *   - `source` may be refined when `payloadSource` matches a defined clock override.
 *   - Unknown `id` returns sentinel `'—'` strings and `iconKey: 'unknown'`.
 *   - Unrecognized `payloadSource` strings fall back to the table default.
 */
export function resolveVerificationLabels(
  id: number | null | undefined,
  payloadSource: string | null | undefined,
  t: (key: string) => string,
): VerificationLabels {
  if (id == null) {
    return {
      method: SENTINEL_METHOD,
      source: SENTINEL_SOURCE,
      methodIconKey: SENTINEL_ICON,
      sourceIconKey: SENTINEL_ICON,
    };
  }

  const entry = VERIFICATION_MODE_TABLE[id];
  if (entry === undefined) {
    return {
      method: SENTINEL_METHOD,
      source: SENTINEL_SOURCE,
      methodIconKey: SENTINEL_ICON,
      sourceIconKey: SENTINEL_ICON,
    };
  }

  // Normalize payloadSource: trim whitespace and lowercase for case-insensitive matching.
  // The backend may store the source in uppercase (e.g. 'CLOCK-WEB') even though we send
  // lowercase. Without normalization the comparison would fall through to the table default.
  const normalizedSource =
    typeof payloadSource === 'string' ? payloadSource.trim().toLowerCase() : null;

  // Resolve source key and icon based on payloadSource overrides
  let resolvedSourceKey: string = entry.sourceKey;
  let resolvedSourceIconKey: VerificationIconKey = entry.sourceIconKey;

  if (normalizedSource === 'clock-web' && entry.sourceWithClockWeb !== undefined) {
    resolvedSourceKey = entry.sourceWithClockWeb;
    resolvedSourceIconKey = entry.sourceIconKeyWithClockWeb ?? SENTINEL_ICON;
  } else if (normalizedSource === 'clock-mobile' && entry.sourceWithClockMobile !== undefined) {
    resolvedSourceKey = entry.sourceWithClockMobile;
    resolvedSourceIconKey = entry.sourceIconKeyWithClockMobile ?? SENTINEL_ICON;
  }
  // Unrecognized payloadSource strings → fall through, use table default

  return {
    method: t(entry.methodKey),
    source: t(resolvedSourceKey),
    methodIconKey: entry.methodIconKey,
    sourceIconKey: resolvedSourceIconKey,
  };
}
